import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './documentStep.module.scss';
import SumsubVerification from '../SumsubVerification';
import { authService } from 'api/auth';
import { ActionButton } from 'components/actionButton';
import kycVerifiedBadge from 'assets/verified-KYC.webp';
import unVerifiedBadge from 'assets/unVerified.webp';

interface DocumentStepProps {
  onBack: () => void;
  isKycVerified: boolean;
}

const DocumentStep: React.FC<DocumentStepProps> = ({ onBack, isKycVerified }) => {
  const { t } = useTranslation();
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const statusConfig = isKycVerified
    ? {
        cardClass: styles.verified,
        badge: kycVerifiedBadge,
        alt: t('settings.verification.kycVerifiedAlt'),
        title: t('settings.verification.identityVerifiedTitle'),
        subtitle: t('settings.verification.identityVerifiedSubtitle'),
      }
    : {
        cardClass: styles.pending,
        badge: unVerifiedBadge,
        alt: t('settings.verification.kycUnverifiedAlt'),
        title: t('settings.verification.identityVerificationTitle'),
        subtitle: t('settings.verification.identityVerificationSubtitle'),
      };

  const handleStartVerification = async () => {
    setIsLoading(true);
    try {
      const response = await authService.getSumsubToken();
      const tokenValue = response?.data?.token;

      if (tokenValue) {
        setToken(tokenValue);
      } else {
        console.error('Invalid token received:', response);
      }
    } catch (error) {
      console.error('Failed to fetch Sumsub token:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.step}>
      <div className={styles.stepHeader}>
        <h3 className={styles.stepTitle}>{t('settings.verification.level2Title')}</h3>

        <button type="button" className={styles.nextStepBtn} onClick={onBack}>
          {t('settings.verification.goToStep1')}
        </button>
      </div>

      {!isKycVerified && token ? (
        <div className={styles.sumsubContainer}>
          <SumsubVerification accessToken={token} />
        </div>
      ) : (
        <div className={`${styles.statusCard} ${statusConfig.cardClass}`}>
          <div className={styles.verifiedContent}>
            <div className={styles.badgeWrapper}>
              <img
                src={statusConfig.badge}
                alt={statusConfig.alt}
                className={styles.verifiedImage}
              />
            </div>
            <div className={styles.textWrapper}>
              <h3 className={styles.verifiedTitle}>{statusConfig.title}</h3>
              <p className={styles.verifiedSubtitle}>{statusConfig.subtitle}</p>
            </div>
          </div>

          {!isKycVerified && (
            <div className={styles.statusBody}>
              <p className={styles.requirementText}>{t('settings.verification.requiresIdSelfie')}</p>
              <ActionButton
                onClick={handleStartVerification}
                className={styles.actionBtn}
                disabled={isLoading}
              >
                {t('settings.verification.verifyNow')}
              </ActionButton>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DocumentStep;
